import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {ROULETTE_QUERY_KEY_MAP} from '../api/roulette/constant';
import {postRouletteGet} from '../api/roulette/postRouletteGet';
import {postRouletteGetWeb} from '../api/roulette/postRouletteGetWeb';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {PagewebError} from '../errors/PagewebError.class';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createRouletteInfo} from '../graphql/factory/RouletteInfo';
import type {
  RouletteInfoQuery,
  RouletteInfoQueryVariables,
} from '../graphql/generated/types';

/**
 * Roulette 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const rouletteFactory = {
  /**
   * 룰렛 정보 조회 (조건부 API)
   *
   * hasMapUid에 따라 다른 API 호출:
   * - hasMapUid=true: postRouletteGetWeb (map_uid 사용)
   * - hasMapUid=false: postRouletteGet (roulette_id 사용)
   */
  rouletteInfo: (
    variables: RouletteInfoQueryVariables,
    options?: Omit<
      UseQueryOptions<RouletteInfoQuery, PagewebError, RouletteInfoQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: ROULETTE_QUERY_KEY_MAP.rouletteInfo(variables),
      queryFn: async () => {
        const response = variables.hasMapUid
          ? await postRouletteGetWeb(
              {
                rouletteId: variables.rouletteId,
                rouletteAdLocId: variables.rouletteAdLocId ?? undefined,
              },
              fetcherOptions
            )
          : await postRouletteGet(
              {
                rouletteId: variables.rouletteId,
                rouletteAdLocId: variables.rouletteAdLocId ?? undefined,
              },
              fetcherOptions
            );

        const {gotcha_info, gotcha_id} = response;
        const {type, entry_type} = gotcha_info ?? {};

        // 방어 코드
        if (!gotcha_info || !gotcha_id || !type || !entry_type) {
          throw createSimpleApiResponseError(
            '화면을 그리는 데에 필요한 필수 데이터가 없습니다.'
          );
        }

        // Adapter의 combine 로직을 여기서 수행
        return {
          rouletteInfo: createRouletteInfo(gotcha_info, gotcha_id),
        };
      },
      ...options,
    }),
} as const;
